import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format, isValid } from 'date-fns';

import { Box, Paragraph, Text, TextButton } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import { DatarouterError } from '@zg-rentals/rental-manager-env';
import {
  useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing,
  useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateListing,
  useRentalManagerUserPropertyVrModelWebApiHandler_associateModel,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { getDefaultTourLabel, getModelId, updateTour } from '../../../../client/utils/3dToursUtils';
import PostingActions from '../../../actions/PostingActions';
import PropertiesActions from '../../../actions/PropertiesActions';
import { mapTourTypeDisplayToEnum } from '../../../utils/tourTypeMapping';
import typeCheckUtils from '../../../utils/typeCheckUtils';

import ErrorMessageConstants from '../../../constants/ErrorMessageConstants';
import NotificationConstants from '../../../constants/NotificationConstants';
import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';
import ExternalTour, { type ExternalTourProps } from '../../../model/ExternalTour';
import VrModel, { type VrModelProps } from '../../../model/VrModel';

import SelectTourModal from '../../Modal/SelectTourModal';
import ViewTourModal from '../../Modal/ViewTourModal';

import { singleTextLineStyles } from '../../../styles';

import TourCardContainer from '../TourCard/TourCardContainer';
import TourCardContentContainer from '../TourCard/TourCardContentContainer';
import TourCardEditableControls from '../TourCard/TourCardEditableControls';
import TourCardImage from '../TourCard/TourCardImage';

import type { FC } from 'react';
import type { ToastRootPropsInterface } from '@zillow/constellation-10';

interface TourCardEditableProps {
  vrModel: VrModelProps;
  externalTour: ExternalTourProps;
  tourType: 'amenity' | 'floorPlan' | 'unit' | 'singleFamily';
  hasBigBottomBorder?: boolean;
  isHidden?: boolean;
  isDisabled?: boolean;
  size?: 'sm' | 'md';
  updateStatusMessage: (message: string, type: ToastRootPropsInterface['tone']) => void;
  floorPlanIndex?: number | null;
  unitIndex?: number | null;
}

interface AssociateParams {
  shouldAssociate: boolean;
  tourId?: string;
  externalTour?: ExternalTourProps;
}

interface RootState {
  user: {
    isMultifamilyCustomer: boolean;
    isSingleFamilyEnterpriseCustomer: boolean;
  };
  properties: {
    currentProperty: {
      vrModels: Array<VrModelProps>;
      syndication: {
        zillow?: {
          displayStatus?: string;
        };
      };
      isMultifamily: boolean;
      listingAlias: string;
      dateLastActivatedTimestamp: string;
      units?: Array<{ vrModel: VrModelProps }>;
      floorplans?: Array<{
        vrModel: VrModelProps;
        units?: Array<{ vrModel: VrModelProps }>;
      }>;
      externalTours: Array<ExternalTourProps>;
    };
  };
}

const isNumeric = typeCheckUtils.isNumeric;

const TourCardEditable: FC<TourCardEditableProps> = ({
  vrModel = VrModel(),
  externalTour = ExternalTour(),
  tourType,
  hasBigBottomBorder = true,
  isHidden = false,
  size = 'sm',
  floorPlanIndex = null,
  unitIndex = null,
  updateStatusMessage,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: associateVrModel } = useRentalManagerUserPropertyVrModelWebApiHandler_associateModel();
  const { mutateAsync: associateVrModelListing } = useRentalManagerUserPropertyVrModelWebApiHandler_associateListing();
  const { mutateAsync: addExternalTourForListing } =
    useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyExternalTourWebApiHandler_addExternalTourForListing',
        },
      },
    });
  const { mutateAsync: removeExternalTourFromListing } =
    useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'useRentalManagerUserPropertyExternalTourWebApiHandler_removeExternalTourFromListing',
        },
      },
    });

  const {
    user: { isMultifamilyCustomer, isSingleFamilyEnterpriseCustomer },
    properties: {
      currentProperty,
      currentProperty: { vrModels, syndication, isMultifamily, listingAlias },
    },
  } = useSelector((state: RootState) => state);

  const updateCurrentProperty = (data: Partial<RootState['properties']['currentProperty']>) =>
    dispatch(PropertiesActions.updateCurrentProperty(data));

  const [showViewTourModal, setShowViewTourModal] = useState(false);
  const [showSelectTourModal, setShowSelectTourModal] = useState(false);

  const isExternalTour = !!externalTour.tourUrl;

  const handleAssociate = ({
    shouldAssociate = false,
    tourId,
    externalTour,
  }: {
    shouldAssociate?: boolean;
    tourId?: string;
    externalTour?: ExternalTourProps;
  }) => {
    if (floorPlanIndex !== null || unitIndex !== null) {
      const modelId = getModelId({
        floorPlanIndex,
        // @ts-expect-error
        currentProperty,
        unitIndex,
      });
      if (tourId) {
        return associateVrModel({
          getParams: {
            vrModelId: Number(tourId),
            listingAlias,
            shouldAssociate: !!shouldAssociate,
            encodedFloorplanKey: modelId ?? '',
          },
        });
      }
      if (externalTour) {
        return dispatch(
          PostingActions.property.externalTour.associateModel(externalTour, listingAlias, shouldAssociate, modelId),
        );
      }
    }
    if (tourId) {
      return associateVrModelListing({
        getParams: { vrModelId: Number(tourId), listingAlias, shouldAssociate: !!shouldAssociate },
      });
    }
    if (externalTour) {
      const tourData = {
        label: externalTour.label,
        tourUrl: externalTour.tourUrl,
        sourceName: externalTour.sourceName,
        tourType: mapTourTypeDisplayToEnum(externalTour.tourType),
        published: true,
      };

      if (shouldAssociate) {
        return addExternalTourForListing({
          getParams: { listingAlias },
          postData: tourData,
        }).then((result) => ExternalTour(result));
      } else {
        return removeExternalTourFromListing({
          getParams: { listingAlias },
          postData: tourData,
        }).then(() => ExternalTour());
      }
    }
    return Promise.resolve();
  };

  const getRemoveAssociateParams = (): AssociateParams => {
    const associateParams: AssociateParams = { shouldAssociate: false };
    if (externalTour.tourUrl) {
      associateParams.externalTour = externalTour;
    } else if (vrModel.vrModelId) {
      associateParams.tourId = vrModel.vrModelId;
    }
    return associateParams;
  };

  const handleRemoveTour = () => {
    const associateParams = getRemoveAssociateParams();
    if (associateParams.tourId || (associateParams.externalTour && associateParams.externalTour.tourUrl)) {
      return (
        handleAssociate(associateParams)
          // @ts-expect-error
          .then(() => {
            updateTour({
              floorPlanIndex,
              // @ts-expect-error
              currentProperty,
              tourToUpdate: isExternalTour ? ExternalTour() : VrModel(),
              tourType,
              previousVrModelId: vrModel.vrModelId,
              previousExternalTourUrl: externalTour.tourUrl,
              unitIndex,
              // @ts-expect-error
              updateCurrentProperty,
              isExternalTour,
            });
            queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
          })
          .catch(() => updateStatusMessage('Cannot remove this 3d tour', NotificationConstants.CRITICAL))
      );
    }
    return Promise.resolve();
  };

  const handleAdd = (isZillowTour: boolean, newSelectedTourId?: string, newExternalTour?: ExternalTourProps) => {
    if (isZillowTour) {
      return (
        handleAssociate({
          shouldAssociate: true,
          tourId: newSelectedTourId,
        })
          // @ts-expect-error
          .then((tour: VrModelProps) => {
            updateTour({
              floorPlanIndex,
              // @ts-expect-error
              currentProperty,
              tourToUpdate: tour,
              tourType,
              unitIndex,
              // @ts-expect-error
              updateCurrentProperty,
              previousVrModelId: vrModel.vrModelId,
              previousExternalTourUrl: externalTour.tourUrl,
              isExternalTour: false,
            });
            setShowViewTourModal(false);
          })
          .catch(() => Promise.reject('Cannot associate this tour, please try again later.'))
      );
    }

    return (
      handleAssociate({
        shouldAssociate: true,
        externalTour: newExternalTour,
      })
        // @ts-expect-error
        .then((tour: VrModelProps) => {
          updateTour({
            floorPlanIndex,
            // @ts-expect-error
            currentProperty,
            tourToUpdate: tour,
            tourType,
            unitIndex,
            // @ts-expect-error
            updateCurrentProperty,
            previousVrModelId: vrModel.vrModelId,
            previousExternalTourUrl: externalTour.tourUrl,
            isExternalTour: true,
          });
          setShowViewTourModal(false);
        })
        .catch((error: unknown) => {
          const errorMessage = DatarouterError.getDisplayMessage(error);
          const lowerCaseMessage = errorMessage.toLowerCase();

          if (lowerCaseMessage.includes('invalid url')) {
            return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_INVALID_URL);
          }
          if (lowerCaseMessage.includes('tour already exists')) {
            return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_ALREADY_EXIST);
          }
          if (lowerCaseMessage.includes('is not from a supported provider')) {
            return Promise.reject(ErrorMessageConstants.EXTERNAL_TOUR_NOT_SUPPORTED_PROVIDER);
          }
          if (lowerCaseMessage.includes('doesn’t have permission')) {
            return Promise.reject(
              ErrorMessageConstants.EXTERNAL_TOUR_NO_PERMISSION(
                isMultifamily && isMultifamilyCustomer,
                isSingleFamilyEnterpriseCustomer,
              ),
            );
          }
          return Promise.reject('Cannot associate this tour, please try again later.');
        })
    );
  };

  const handleSelectTourSubmit = (
    isZillowTour: boolean,
    newSelectedTourId?: string,
    newExternalTour?: ExternalTourProps,
  ) => {
    if (!newSelectedTourId && !newExternalTour?.tourUrl) {
      setShowSelectTourModal(false);
      return handleRemoveTour();
    }

    const isVrModelUpdated = newSelectedTourId && newSelectedTourId !== vrModel.vrModelId;
    const isExternalTourUpdated =
      newExternalTour &&
      (newExternalTour.tourUrl !== externalTour.tourUrl || newExternalTour.label !== externalTour.label);
    if (isVrModelUpdated || isExternalTourUpdated) {
      return (
        handleAssociate(getRemoveAssociateParams())
          // @ts-expect-error
          .then(() => handleAdd(isZillowTour, newSelectedTourId, newExternalTour))
      );
    }

    setShowSelectTourModal(false);
    return Promise.resolve();
  };

  const handleViewTour = () => {
    if (isExternalTour) {
      window.open(externalTour.tourUrl, '_blank');
    } else {
      setShowViewTourModal(true);
    }
  };

  const renderImage = () => {
    return <TourCardImage imageSrc={vrModel.posterUrl} isHidden={isHidden} size={size} />;
  };

  const renderContent = () => {
    let title = vrModel.label || '';
    let { createDateTimeUtc = '' } = vrModel;
    let source = 'Zillow 3D Home app';
    let isVrModelStatsVisible = isNumeric(vrModel.totalTourViews) && Number(vrModel.totalTourViews) > 0;
    const averageViewDuration = new Date(0);
    averageViewDuration.setSeconds(vrModel.averageViewDuration || 0);
    if (externalTour.tourUrl !== '') {
      title = externalTour.label || '';
      createDateTimeUtc = String(externalTour.createdTimestamp || '');
      source = externalTour.sourceName;
      isVrModelStatsVisible = false;
    }

    return (
      <>
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <Paragraph textStyle="body-bold" css={{ ...singleTextLineStyles, flex: 1 }}>
            {title}
          </Paragraph>
          <TourCardEditableControls onRemoveTour={handleRemoveTour} onViewTour={handleViewTour} />
        </Box>
        <Box
          css={{
            padding: '{spacing.200} 0 {spacing.400} 0',
            display: 'flex',
            visibility: isVrModelStatsVisible ? 'visible' : 'hidden',
          }}
        >
          <Box css={{ paddingRight: '200' }}>
            <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
              <b>{vrModel.totalTourViews}</b>
              <Text textStyle="body-xs" color="text.subtle">
                {' views'}
              </Text>
            </Paragraph>
          </Box>
          {isValid(averageViewDuration) && (
            <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
              <b>{format(averageViewDuration, 'm:ss')}</b>
              <Text textStyle="body-xs" color="text.subtle">
                {' avg. viewing time'}
              </Text>
            </Paragraph>
          )}
        </Box>
        <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
          {'Source: '}
          <TextButton textStyle="body-sm" onClick={handleViewTour}>
            {source}
          </TextButton>
        </Paragraph>
        <Paragraph textStyle="body-xs" css={{ ...singleTextLineStyles }}>
          {`Created: ${createDateTimeUtc && isValid(new Date(Number(createDateTimeUtc))) ? format(new Date(Number(createDateTimeUtc)), 'MM/dd/yyyy') : '-'}`}
        </Paragraph>
      </>
    );
  };

  return (
    <>
      <TourCardContainer hasBigBottomBorder={hasBigBottomBorder} size={size}>
        {renderImage()}
        <TourCardContentContainer size={size}>{renderContent()}</TourCardContentContainer>
      </TourCardContainer>
      {showViewTourModal && (
        <ViewTourModal
          vrModel={{ ...vrModel, name: vrModel.label || '', tourUrl: vrModel.tourUrl || '' }}
          onClose={() => setShowViewTourModal(false)}
        />
      )}
      {showSelectTourModal && (
        // @ts-expect-error
        <SelectTourModal
          tourType={tourType}
          initialSelectedVrModelId={vrModel.vrModelId || null}
          initialExternalTour={{
            ...externalTour,
            label:
              vrModel.vrModelId || externalTour.tourUrl
                ? externalTour.label
                : // @ts-expect-error
                  getDefaultTourLabel(tourType, floorPlanIndex, unitIndex, currentProperty),
          }}
          initialTab={externalTour.tourUrl ? 'external-tour-tab' : 'zillow-tour-tab'}
          onSubmit={handleSelectTourSubmit}
          onClose={() => setShowSelectTourModal(false)}
          zillowDisplayStatus={syndication?.zillow?.displayStatus?.toLowerCase()}
          filterOutVrModelIds={vrModels.map((vrModel) => vrModel.vrModelId)}
        />
      )}
    </>
  );
};

export type { TourCardEditableProps };
export default TourCardEditable;
